import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
} from "mobx";
import { groupLens, LensGroup } from "@/modules/timeline";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import {
  ItemListItemKind,
  ItemListItemRowData,
  ItemListRowData,
  ItemListRowType,
} from "@/components/item-list/types";
import { generateShortDateString } from "@/domains/date/date";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { INoteObservable } from "@/store/note";
import { DeleteNotePermanentlyModalStore } from "@/components/modal/delete-notes-permanently/delete-notes-permanently-modal-store";
import { ItemPreviewState } from "@/components/item-preview/item-preview-state";
import { ItemListRowFeaturedContent } from "@/components/item-list/rows/components/item-list-row-featured-content";
import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import { Subscription } from "node_modules/react-hook-form/dist/utils/createSubject";
import Dexie, { liveQuery } from "dexie";
import { EventContext } from "@/domains/metrics/context";
import { MDSIconKind, MDSMenuContentItemKind, MDSMenuContentList } from "@/mds";
import { itemsModule } from "@/modules/items";

type TrashItemIndexTuple = [trashed_at: string, model_id: string];

export class TrashPageStore extends AppSubStore {
  listState: ListStateObservable;
  deleteNotePermanentlyModal: DeleteNotePermanentlyModalStore;
  supportsRestoreListAction = true;
  supportsMoveNoteToTrashListAction = true;
  showConfirmModal = false;

  liveQuerySubscription?: Subscription;
  subscribedItems: TrashItemIndexTuple[] = [];

  eventContext = EventContext.TrashMultiselectActions;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.deleteNotePermanentlyModal = new DeleteNotePermanentlyModalStore(injectedDeps, {});
    this.listState = new ListStateObservable({
      ...injectedDeps,
      listStateProvider: this,
      deleteNotePermanentlyModal: this.deleteNotePermanentlyModal,
    });

    makeObservable(this, {
      subscribedItems: observable,
      liveQuerySubscription: observable,
      unsubscribeLiveQuery: action,
      subscribeLiveQuery: action,

      items: computed,
      itemRows: computed,
      orderedItemIds: computed,
      generateItemRow: false,
      generateItemDropdown: false,

      listState: false,
      deleteNotePermanentlyModal: false,
      supportsRestoreListAction: observable,
      supportsMoveNoteToTrashListAction: false,
      showConfirmModal: observable,
      openConfirmModal: action,
      closeConfirmModal: action,
      emptyTrash: action,
      isEmptyingTrash: computed,

      eventContext: false,
    });

    onBecomeObserved(this, "subscribedItems", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "subscribedItems", () => this.unsubscribeLiveQuery());
  }

  openConfirmModal = () => {
    this.showConfirmModal = true;
  };

  closeConfirmModal = () => {
    this.showConfirmModal = false;
  };

  emptyTrash = async () => {
    for (const row of this.itemRows) {
      if (row.type !== ItemListRowType.AsyncNote) continue;
      // TODO: Implement a single action to delete all notes on the backend.
      await this.store.notes.deleteNote({ noteId: row.payload.noteId });
    }

    this.closeConfirmModal();
  };

  get isEmptyingTrash() {
    // Assuming any note deletion is the result of emptyTrash.
    const deleteNoteOperations =
      this.store.sync.actionQueue.operationsByOperationKind.get("DELETE_NOTE");
    if (!deleteNoteOperations) return false;
    return deleteNoteOperations.length > 0;
  }

  generateItemDropdown({
    noteObservable,
  }: {
    noteObservable: INoteObservable;
  }): MDSMenuContentList {
    return [
      {
        id: `restore-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Redo,
        label: "Restore this note",
        onClick: async () => await noteObservable.restoreFromTrash(),
      },
      {
        id: `delete-permanently-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Trash,
        label: "Delete this note forever",
        onClick: () => this.deleteNotePermanentlyModal.open({ notes: [noteObservable] }),
      },
    ];
  }

  subscribeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(() => {
      return this.store.notes.localTable
        .where("[trashed_at+model_id]")
        .above(["\0", Dexie.minKey])
        .reverse()
        .keys();
    }).subscribe({
      next: ids =>
        runInAction(() => {
          this.subscribedItems = ids as unknown as TrashItemIndexTuple[];
        }),
    });
  }

  unsubscribeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
  }

  get items(): LensGroup<TrashItemIndexTuple>[] {
    const items = this.subscribedItems.map(item => ({
      dateTime: new Date(item[0]),
      item: item,
    }));
    return groupLens(items);
  }

  generateItemRow(note: INoteObservable): ItemListItemRowData {
    const itemAppearance = itemsModule.resolveAppearance({ itemId: note.id, appStore: this.store });

    return {
      type: ItemListRowType.Item,
      key: note.id,
      payload: {
        id: note.id,
        kind: ItemListItemKind.Note,
        titleIcon: note.isShared ? { kind: MDSIconKind.Shared } : undefined,
        appearance: itemAppearance,
        createPreviewState: () =>
          new ItemPreviewState({
            store: this.store,
            id: note.id,
            kind: ItemListItemKind.Note,
          }),
        label: note.title,
        onClick: () => this.store.navigation.goToNote({ noteId: note.id }),
        sharedBy: note.sharedBy,
        dateLabel: generateShortDateString(note.trashedAt || ""),
        listState: this.listState,
        dropdown: this.generateItemDropdown({ noteObservable: note }),
        chips: () => {
          const sortedCollections = sortCollectionsForChips(
            note.collectionList?.allCollections || []
          );
          if (sortedCollections.length === 0) return null;
          return <ItemListRowFeaturedContent inline collections={sortedCollections} />;
        },
        extraRow: {
          id: `note-${note.id}-content`,
          content: () => (
            <ItemListRowFeaturedContent
              snippet={[{ text: note.secondaryTitle || "No additional text" }]}
            />
          ),
        },
      },
    };
  }

  get itemRows(): ItemListRowData[] {
    const output: ItemListRowData[] = [];
    for (const group of this.items) {
      // Add header for each section
      output.push({
        type: ItemListRowType.SectionHeader,
        key: group.title,
        payload: { title: group.title },
      });

      // Add each item afterwards
      for (const item of group.items) {
        output.push({
          type: ItemListRowType.AsyncNote,
          key: item.item[1],
          payload: {
            noteId: item.item[1],
            itemRow: (note: INoteObservable) => this.generateItemRow(note),
          },
        });
      }
    }

    if (output.length) {
      output.push({
        type: ItemListRowType.Padding,
        key: "padding",
        payload: { height: 50 },
      });
    }
    return output;
  }

  get orderedItemIds() {
    return this.itemRows.map(row => row.key);
  }
}
